/*******************************************************************************
 * More Info Dialog Component
 *
 * A reusable button component that opens a modal with additional information.
 * Can be configured as an icon button or text button, with customizable modal
 * size and content.
 *******************************************************************************/
import angular from 'angular';
import uiBootstrap from 'angular-ui-bootstrap';
import ngSanitize from 'angular-sanitize';
import moreInfoDialogTemplate from './moreInfoDialog.template.html';
import './moreInfoDialog.scss';

export default angular
  .module('csm.ui.moreInfoDialog', [uiBootstrap, ngSanitize])

  /*****************************************************************************
   * Directive: moreInfoDialog
   *
   * Creates a button that opens a modal with additional information.
   *
   * Attributes:
   * - data: Object or array of key-value pairs to display
   * - title: Title for the modal (default: "More Information")
   * - size: Modal size ('sm', 'md', 'lg') (default: 'md')
   * - icon-only: Boolean, if true shows icon button, else text button with icon (default: false)
   * - custom-body: Custom HTML content for modal body
   * - key-field: Property name to use as key for array objects (default: 'key')
   * - value-field: Property name to use as value for array objects (default: 'value')
   *
   * Example usage:
   * <more-info-dialog
   *      data="legendData"
   *      title="Report Legend"
   *      size="lg"
   *      icon-only="true">
   * </more-info-dialog>
   *
   * <more-info-dialog
   *      data="statusData"
   *      title="Status Explanations"
   *      key-field="label"
   *      value-field="explanation">
   * </more-info-dialog>
   *****************************************************************************/
  .directive('moreInfoDialog', moreInfoDialogDirective).name;

/* @ngInject */
function moreInfoDialogDirective($uibModal) {
  return {
    restrict: 'E',
    scope: {
      data: '=',
      title: '@',
      size: '@',
      iconOnly: '=',
      customBody: '=',
      keyField: '@',
      valueField: '@'
    },
    template: `
      <button type="button" 
              class="btn btn-info more-info-btn" 
              ng-click="openModal()"
              title="Click for More Information"
              aria-label="Click for More Information">
        <i ng-if="iconOnly" class="glyphicon glyphicon-info-sign"></i>
        <span ng-if="!iconOnly">
          <i class="glyphicon glyphicon-info-sign"></i> Info
        </span>
      </button>
    `,
    link: function (scope) {
      // Set default values
      scope.title = scope.title || 'More Information';
      scope.size = scope.size || 'md';
      scope.iconOnly = scope.iconOnly === true;
      scope.keyField = scope.keyField || 'key';
      scope.valueField = scope.valueField || 'value';

      /**
       * Opens the modal with the configured data
       */
      scope.openModal = function () {
        $uibModal.open({
          template: moreInfoDialogTemplate,
          size: scope.size,
          windowClass: 'more-info-dialog',
          backdrop: 'static',
          keyboard: true,
          controller: MoreInfoDialogController,
          resolve: {
            modalData: function () {
              return {
                title: scope.title,
                data: scope.data,
                customBody: scope.customBody,
                keyField: scope.keyField,
                valueField: scope.valueField
              };
            }
          }
        });
      };
    }
  };
}

/*****************************************************************************
 * Controller: MoreInfoDialogController
 *
 * Controller for the More Info Dialog modal dialog
 *****************************************************************************/
/* @ngInject */
function MoreInfoDialogController(
  $scope,
  $uibModalInstance,
  $sce,
  $sanitize,
  modalData
) {
  $scope.title = modalData.title;
  $scope.data = modalData.data;

  // Sanitize HTML first to remove dangerous content, then trust it for SCE
  if (modalData.customBody) {
    const sanitizedHtml = $sanitize(modalData.customBody);
    $scope.customBody = $sce.trustAsHtml(sanitizedHtml);
  } else {
    $scope.customBody = null;
  }

  $scope.keyField = modalData.keyField || 'key';
  $scope.valueField = modalData.valueField || 'value';

  // Check if data is an array of objects or a simple object
  $scope.isDataArray = angular.isArray($scope.data);
  $scope.isDataObject =
    angular.isObject($scope.data) && !angular.isArray($scope.data);

  /**
   * Convert object to key-value pairs for display
   */
  if ($scope.isDataObject) {
    $scope.dataItems = Object.keys($scope.data).map(function (key) {
      return {
        key: key,
        value: $scope.data[key]
      };
    });
  }

  /**
   * Convert array items to use specified key/value fields
   */
  if ($scope.isDataArray) {
    $scope.dataItems = $scope.data
      .map(function (item) {
        return {
          key: item[$scope.keyField],
          value: item[$scope.valueField]
        };
      })
      .filter(function (item) {
        // Only include items that have both key and value
        return item.key !== undefined && item.value !== undefined;
      });
  }

  /**
   * Close the modal
   */
  $scope.close = function () {
    $uibModalInstance.dismiss('cancel');
  };
}
